import { supabase } from '@/integrations/supabase/client';
import { FunctionsHttpError } from '@supabase/supabase-js';

export type Venue = 'kalshi' | 'polymarket';

export interface VenueConnection {
  venue: Venue;
  public_meta: Record<string, unknown> | null;
  updated_at: string;
}

export interface OrderIntent {
  market_id: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  limit_price_cents: number;
  size: number;
  token_id?: string;
}

async function invoke<T>(fn: string, body?: unknown, method: 'GET' | 'POST' = 'POST'): Promise<T> {
  const { data, error } = await supabase.functions.invoke(fn, {
    method,
    ...(body === undefined || body === null ? {} : { body: body as Record<string, unknown> }),
  });
  if (error) {
    let detail = error.message;
    if (error instanceof FunctionsHttpError) {
      const text = await error.context.text();
      try {
        detail = (JSON.parse(text) as { error?: string })?.error ?? text;
      } catch {
        detail = text || detail;
      }
    }
    throw new Error(detail);
  }
  return data as T;
}

export const venueTradingService = {
  /** Which venues the signed-in user has connected. Empty when backend isn't live yet. */
  async listConnections(): Promise<VenueConnection[]> {
    try {
      const res = await invoke<{ connections: VenueConnection[] }>('kalshi-connect', undefined, 'GET');
      return res?.connections ?? [];
    } catch {
      return [];
    }
  },

  connectKalshi(keyId: string, privateKeyPem: string) {
    return invoke<{ ok: boolean; balance_cents: number | null }>('kalshi-connect', {
      key_id: keyId,
      private_key_pem: privateKeyPem,
    });
  },

  connectPolymarket(privateKey: string) {
    return invoke<{ ok: boolean; address: string }>('polymarket-connect', {
      private_key: privateKey,
    });
  },

  disconnect(venue: Venue) {
    const fn = venue === 'kalshi' ? 'kalshi-connect' : 'polymarket-connect';
    return invoke<{ ok: boolean }>(fn, { action: 'disconnect', venue });
  },

  placeOrder(venue: Venue, intent: OrderIntent) {
    const fn = venue === 'kalshi' ? 'kalshi-order' : 'polymarket-order';
    return invoke<{ ok: boolean; order: unknown }>(fn, intent);
  },

  portfolio(venue: Venue) {
    const fn = venue === 'kalshi' ? 'kalshi-portfolio' : 'polymarket-portfolio';
    return invoke<Record<string, unknown>>(fn, undefined, 'GET');
  },
};
